import { useEffect, useId, useRef, useState, type FormEvent, type InputHTMLAttributes, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Wordmark } from "../runner/Wordmark";
import "../runner/runner.css";
import "./auth.css";
import { ApiError, callFunction, errorMessage } from "../lib/api";
import { supabase } from "../lib/supabase";
import { Banner, Field, Spinner } from "../ui/kit";
import { useAuth } from "./AuthProvider";
import { MAX_PASSWORD, passwordRules, passwordStrength, STRENGTH_LABEL } from "./password";
import { CATALOGUE } from "../runner/plans";

// The way in: #/signup (account → 6-digit code → panel), #/login, #/forgot-password (email → code → new password →
// panel). Email is the account's one identifier; codes come by email from Supabase Auth. The routes and their
// guards are in App.tsx. The code step and the new-password fields are shared with Account → Change password
// (auth/Account.tsx), which uses the same emailed code: there is no other way to change a password.

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_SECONDS = 60;
const OFFLINE = "You're offline or Keept's server can't be reached. Nothing was lost; try again.";

const cleanEmail = (raw: string) => raw.trim().toLowerCase();

/** Supabase Auth's errors in plain words. */
export function authError(err: { message: string; status?: number; code?: string }): string {
  if (err.code === "invalid_credentials" || err.message.includes("Invalid login")) return "Email or password isn't right.";
  if (err.code === "otp_expired" || /expired or is invalid/i.test(err.message)) return "That code isn't right or has expired. Check the email, or ask for a new code.";
  if (err.code === "over_request_rate_limit" || err.status === 429) return "Too many tries just now. Wait a minute and try again.";
  if (!err.status || /fetch|network/i.test(err.message)) return OFFLINE;
  return err.message;
}

// ---- Layout ---------------------------------------------------------------------------------------

function AuthPage({ children, below, wide }: { children: ReactNode; below?: ReactNode; wide?: boolean }) {
  return (
    <div className="kr-auth ka">
      <header className="ka-top">
        <span className="ka-brand"><Wordmark mark size={20} /></span>
      </header>
      <main className={`ka-main${wide ? " wide" : ""}`}>
        <div className="ka-card">{children}</div>
        {below && <p className="ka-below">{below}</p>}
      </main>
      <footer className="ka-foot">Questions? <a href="mailto:kept@usekeept.com">kept@usekeept.com</a> · <a href={LEGAL.terms} target="_blank" rel="noopener noreferrer">Terms</a> · <a href={LEGAL.privacy} target="_blank" rel="noopener noreferrer">Privacy</a> · <a href={LEGAL.security} target="_blank" rel="noopener noreferrer">Security</a></footer>
    </div>
  );
}

export function Heading({ title, sub }: { title: string; sub?: ReactNode }) {
  return (
    <div className="ka-head">
      <h1>{title}</h1>
      {sub && <p>{sub}</p>}
    </div>
  );
}

function TextField({ label, hint, error, aside, ...input }: { label: string; hint?: ReactNode; error?: string | null; aside?: ReactNode } & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <div className="ka-field">
      <div className="ka-label-row">
        <label htmlFor={id}>{label}</label>
        {aside}
      </div>
      <input id={id} className={`ka-input${error ? " bad" : ""}`} aria-invalid={error ? true : undefined} aria-describedby={error ? `${id}-err` : undefined} {...input} />
      {error && <span className="ka-field-err" id={`${id}-err`} role="alert">{error}</span>}
      {hint && !error && <span className="ka-hint">{hint}</span>}
    </div>
  );
}

export function Alert({ children, tone = "warn" }: { children: ReactNode; tone?: "warn" | "info" }) {
  return <div className={`ka-alert ${tone}`} role={tone === "warn" ? "alert" : "status"}>{children}</div>;
}

export function Submit({ busy, disabled, children }: { busy: boolean; disabled?: boolean; children: ReactNode }) {
  return <button type="submit" className="ka-btn primary" disabled={busy || disabled}>{busy && <Spinner />}{children}</button>;
}

// ---- Password fields with the meter -----------------------------------------------------------------

/** The strength bar, its label and the rule checklist (web/src/auth/password.ts, the server's rule). */
export function PasswordMeter({ password }: { password: string }) {
  const strength = passwordStrength(password);
  const filled = !password ? 0 : strength === "weak" ? 1 : strength === "medium" ? 2 : 3;
  return (
    <div className="ka-meter" data-strength={password ? strength : "empty"}>
      <div className="ka-meter-row">
        <div className="ka-bar" aria-hidden="true">{[0, 1, 2].map((i) => <span key={i} className={i < filled ? "on" : ""} />)}</div>
        <span className="ka-meter-label" aria-live="polite">{password ? STRENGTH_LABEL[strength] : " "}</span>
      </div>
      <ul className="ka-rules">
        {passwordRules(password).map((r) => (
          <li key={r.id} className={r.ok ? "ok" : ""}>
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
              {r.ok ? <path d="M3.5 7.2l2.3 2.3 4.7-4.9" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /> : <circle cx="7" cy="7" r="2" fill="currentColor" />}
            </svg>
            <span>{r.label}</span><span className="ka-sr">{r.ok ? " (met)" : " (not met yet)"}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function NewPasswordFields({ password, setPassword, confirm, setConfirm, labels = ["Password", "Confirm password"] }: {
  password: string; setPassword: (v: string) => void; confirm: string; setConfirm: (v: string) => void; labels?: [string, string];
}) {
  return (
    <>
      <div className="ka-field-group">
        <TextField label={labels[0]} type="password" name="new-password" autoComplete="new-password" maxLength={MAX_PASSWORD} value={password} onChange={(e) => setPassword(e.target.value)} required />
        <PasswordMeter password={password} />
      </div>
      <TextField label={labels[1]} type="password" name="confirm-password" autoComplete="new-password" maxLength={MAX_PASSWORD} value={confirm} onChange={(e) => setConfirm(e.target.value)}
        error={confirm && confirm !== password ? "Passwords don't match" : null} required />
    </>
  );
}

// ---- The 6-digit code --------------------------------------------------------------------------------

/** Six boxes over one real input: typing, paste and the phone's one-time-code fill all land in it. */
export function CodeInput({ value, onChange, disabled }: { value: string; onChange: (v: string) => void; disabled?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="ka-code" onClick={() => ref.current?.focus()}>
      <input ref={ref} className="ka-code-input" aria-label="6-digit code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]*" maxLength={6}
        value={value} disabled={disabled} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))} />
      {Array.from({ length: 6 }, (_, i) => (
        <span key={i} aria-hidden="true" className={`ka-code-cell${focused && i === Math.min(value.length, 5) ? " active" : ""}${value[i] ? " full" : ""}`}>{value[i] ?? ""}</span>
      ))}
    </div>
  );
}

function useCountdown(initial: number) {
  const [left, setLeft] = useState(initial);
  useEffect(() => {
    if (left <= 0) return;
    const t = window.setTimeout(() => setLeft((s) => s - 1), 1000);
    return () => window.clearTimeout(t);
  }, [left]);
  return [left, setLeft] as const;
}

export interface SendResult { sent: boolean; notice?: string; retryAfter?: number }

/**
 * Enter the emailed code, with "Resend code" (60 s apart, as Supabase allows) and a way back to change the email.
 * `onVerify` gets the 6 digits; it throws a readable error or resolves once the session exists.
 */
export function CodeStep({ email, title, sub, firstSend, onVerify, onResend, onBack, backLabel }: {
  email: string; title: string; sub: ReactNode; firstSend: SendResult | null;
  onVerify: (code: string) => Promise<void>; onResend: () => Promise<SendResult>; onBack: () => void; backLabel: string;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(firstSend && !firstSend.sent ? firstSend.notice ?? null : null);
  const [left, setLeft] = useCountdown(firstSend ? (firstSend.sent ? RESEND_SECONDS : firstSend.retryAfter ?? 0) : 0);
  const [resending, setResending] = useState(false);
  const tried = useRef("");

  const verify = async (digits: string) => {
    if (digits.length !== 6 || busy) return;
    tried.current = digits;
    setBusy(true);
    setError(null);
    try {
      await onVerify(digits);
    } catch (e) {
      setError(errorMessage(e));
      setBusy(false);
    }
  };
  // The sixth digit (typed or pasted) sends it; the same wrong code isn't sent twice in a row.
  useEffect(() => {
    if (code.length === 6 && code !== tried.current) void verify(code);
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  const resend = async () => {
    setResending(true);
    setError(null);
    try {
      const r = await onResend();
      if (r.sent) {
        setNotice(null);
        setLeft(RESEND_SECONDS);
      } else {
        setNotice(r.notice ?? "We couldn't send the code email just now. Try again in a minute.");
        setLeft(r.retryAfter ?? RESEND_SECONDS);
      }
      setCode("");
      tried.current = "";
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setResending(false);
    }
  };

  return (
    <form className="ka-form" onSubmit={(e) => { e.preventDefault(); void verify(code); }}>
      <Heading title={title} sub={sub} />
      <div className="ka-email-chip"><span>{email}</span><button type="button" className="ka-link" onClick={onBack}>{backLabel}</button></div>
      <CodeInput value={code} onChange={(v) => { setCode(v); if (v.length < 6) setError(null); }} disabled={busy} />
      {error && <Alert>{error}</Alert>}
      {notice && <Alert tone="info">{notice}</Alert>}
      <Submit busy={busy} disabled={code.length !== 6}>Continue</Submit>
      <div className="ka-resend" aria-live="polite">
        {left > 0
          ? <span>Didn't get it? You can resend the code in <b className="num">0:{String(left).padStart(2, "0")}</b></span>
          : <span>Didn't get it? <button type="button" className="ka-link" disabled={resending} onClick={() => void resend()}>{resending ? "Sending…" : "Resend code"}</button></span>}
      </div>
      <p className="ka-fine">The code expires in one hour. Check your spam folder if it isn't in your inbox.</p>
    </form>
  );
}

// ---- The authenticator app's code (two-step verification) ----------------------------------------------

/** Supabase Auth's two-step verification errors in plain words. */
export function appCodeError(err: { message: string; status?: number; code?: string }): string {
  if (err.code === "mfa_verification_failed" || /invalid totp|invalid code/i.test(err.message)) return "That code isn't right. Codes change every 30 seconds: enter the one your app shows now.";
  if (err.code === "mfa_challenge_expired" || /expired/i.test(err.message)) return "That took too long. Enter the code your app shows now.";
  return authError(err);
}

/**
 * Six boxes for the code an authenticator app shows (Google Authenticator, Microsoft Authenticator, 1Password…).
 * The sixth digit sends it; the same wrong code isn't sent twice in a row. `onCode` throws a readable error.
 */
export function AppCodeForm({ onCode, submitLabel, children, after, className }: { onCode: (code: string) => Promise<void>; submitLabel: string; children?: ReactNode; after?: ReactNode; className?: string }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tried = useRef("");
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const verify = async (digits: string) => {
    if (digits.length !== 6 || busy) return;
    tried.current = digits;
    setBusy(true);
    setError(null);
    try {
      await onCode(digits);
    } catch (e) {
      if (alive.current) setError(errorMessage(e));
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  useEffect(() => {
    if (code.length === 6 && code !== tried.current) void verify(code);
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <form className={className ?? "ka-form"} onSubmit={(e) => { e.preventDefault(); void verify(code); }}>
      {children}
      <CodeInput value={code} onChange={(v) => { setCode(v); if (v.length < 6) { setError(null); tried.current = ""; } }} disabled={busy} />
      {error && <Alert>{error}</Alert>}
      <Submit busy={busy} disabled={code.length !== 6}>{submitLabel}</Submit>
      {after}
    </form>
  );
}

/** Asks the auth server to check a code against the account's authenticator factor; the session becomes aal2. */
export async function verifyAppCode(code: string): Promise<void> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) throw new Error(authError(error));
  const factor = data.totp.find((f) => f.status === "verified");
  if (!factor) throw new Error("Two-step verification isn't on for this account any more. Log in again.");
  const { error: err } = await supabase.auth.mfa.challengeAndVerify({ factorId: factor.id, code });
  if (err) throw new Error(appCodeError(err));
}

/**
 * After the password, for an account with two-step verification: the 6-digit code from the authenticator app, then
 * the panel (App.tsx shows this instead of any signed-in screen while the session is aal1). A lost phone: write to
 * kept@usekeept.com from the account's address (supabase/scripts/remove-mfa.mjs).
 */
export function MfaChallenge() {
  const { session, signOut } = useAuth();
  const email = session?.user.email ?? "";
  return (
    <AuthPage below={<>Lost your phone? Write to <a className="ka-link" href="mailto:kept@usekeept.com">kept@usekeept.com</a> from your account's email and we'll help you back in.</>}>
      <AppCodeForm submitLabel="Continue" onCode={verifyAppCode}>
        <Heading title="Enter your code" sub="Open your authenticator app and enter the 6-digit code it shows for Keept Runner." />
        <div className="ka-email-chip"><span>{email}</span><button type="button" className="ka-link" onClick={() => void signOut()}>Log out</button></div>
      </AppCodeForm>
    </AuthPage>
  );
}

// ---- Log in ------------------------------------------------------------------------------------------

export function LogIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(() => (location.state as { email?: string } | null)?.email ?? "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unconfirmed, setUnconfirmed] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setUnconfirmed(null);
    const address = cleanEmail(email);
    const { error: err } = await supabase.auth.signInWithPassword({ email: address, password });
    setBusy(false);
    if (!err) return; // the guard takes the session to the panel
    if (err.code === "email_not_confirmed" || /not confirmed/i.test(err.message)) setUnconfirmed(address);
    else setError(authError(err));
  };
  return (
    <AuthPage below={<>Don't have an account? <Link className="ka-link" to="/signup">Start free</Link></>}>
      <form className="ka-form" onSubmit={submit}>
        <Heading title="Log in to Keept Runner" />
        <TextField label="Email" type="email" name="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
        <TextField label="Password" type="password" name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required
          aside={<Link className="ka-link small" to="/forgot-password" state={{ email: cleanEmail(email) }}>Forgot password?</Link>} />
        {error && <Alert>{error}</Alert>}
        {unconfirmed && (
          <Alert>
            This email isn't confirmed yet. Enter the 6-digit code we emailed to <b>{unconfirmed}</b> when you signed up.{" "}
            <button type="button" className="ka-link" onClick={() => navigate("/signup", { state: { confirm: unconfirmed } })}>Enter the code</button>
          </Alert>
        )}
        <Submit busy={busy}>Log in</Submit>
      </form>
    </AuthPage>
  );
}

// ---- Sign up -----------------------------------------------------------------------------------------

/** The legal pages on the site (public, no sign-in): creating an account is agreeing to the Terms (version kept on the server). */
export const LEGAL = { terms: "https://usekeept.com/terms", privacy: "https://usekeept.com/privacy", security: "https://usekeept.com/security" } as const;

export function SignUp() {
  const location = useLocation();
  const navigate = useNavigate();
  const confirmOnly = (location.state as { confirm?: string } | null)?.confirm ?? null;
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exists, setExists] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  // The code step: the address, and how the first send went (null = sent earlier, e.g. coming from Log in).
  const [codeFor, setCodeFor] = useState<{ email: string; first: SendResult | null } | null>(confirmOnly ? { email: confirmOnly, first: null } : null);

  const emailOk = EMAIL.test(cleanEmail(email));
  const valid = name.trim().length > 0 && emailOk && passwordStrength(password) !== "weak" && confirm === password;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    setExists(false);
    try {
      const address = cleanEmail(email);
      const r = await callFunction<SendResult & { email: string }>("account-auth", {
        action: "signup", email: address, password, confirmPassword: confirm, displayName: name.trim(), company: company.trim(),
      }, { auth: false });
      setCodeFor({ email: address, first: r });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setExists(true);
      else setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (codeFor) {
    return (
      <AuthPage>
        <CodeStep email={codeFor.email} firstSend={codeFor.first}
          title="Check your email"
          sub={codeFor.first ? "We sent a 6-digit code to confirm your email. Enter it to open Keept Runner." : "Confirm your email to finish signing up. Enter the 6-digit code we sent, or ask for a new one."}
          onVerify={async (token) => {
            const { error: err } = await supabase.auth.verifyOtp({ email: codeFor.email, token, type: "signup" });
            if (err) throw new Error(authError(err));
            // Signed in: the guard opens the panel.
          }}
          onResend={() => callFunction<SendResult>("account-auth", { action: "resend", email: codeFor.email, type: "signup" }, { auth: false })}
          onBack={() => { setCodeFor(null); if (confirmOnly) navigate("/signup", { replace: true, state: null }); }}
          backLabel="Change email" />
      </AuthPage>
    );
  }

  return (
    <AuthPage wide below={<>Already have an account? <Link className="ka-link" to="/login">Log in</Link></>}>
      <form className="ka-form" onSubmit={submit} noValidate>
        <Heading title="Create your account" sub={`Keept Runner for accounting firms on QuickBooks Online Accountant and Intuit Accountant Suite. No card needed: up to ${CATALOGUE.trialCommandsPerDay} commands a day.`} />
        <TextField label="Name" name="name" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} required autoFocus />
        <TextField label="Work email" type="email" name="email" autoComplete="email" value={email} onChange={(e) => { setEmail(e.target.value); setExists(false); }} required
          onBlur={() => setEmailTouched(true)} error={emailTouched && email && !emailOk ? "Enter a valid email address." : null} />
        {exists && (
          <Alert>An account with this email already exists — <Link className="ka-link" to="/login" state={{ email: cleanEmail(email) }}>log in</Link>.</Alert>
        )}
        <NewPasswordFields password={password} setPassword={setPassword} confirm={confirm} setConfirm={setConfirm} />
        <TextField label="Company" aside={<span className="ka-optional">Optional</span>} name="organization" autoComplete="organization" value={company} onChange={(e) => setCompany(e.target.value)} maxLength={120} />
        {error && <Alert>{error}</Alert>}
        <p className="ka-fine ka-consent">By creating an account you confirm you're 18 or older and agree to the <a className="ka-link" href={LEGAL.terms} target="_blank" rel="noopener noreferrer">Terms of Service</a> and the <a className="ka-link" href={LEGAL.privacy} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p>
        <Submit busy={busy} disabled={!valid || exists}>Create account</Submit>
        <p className="ka-fine">We'll email you a 6-digit code to confirm the address.</p>
      </form>
    </AuthPage>
  );
}

// ---- Forgot password ---------------------------------------------------------------------------------

export function ForgotPassword() {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, resetting, setResetting, signOut } = useAuth();
  const [step, setStep] = useState<"email" | "code" | "password" | "done">(() => (resetting && session ? "password" : "email"));
  const [email, setEmail] = useState(() => (resetting && session?.user.email) || ((location.state as { email?: string } | null)?.email ?? ""));
  const [first, setFirst] = useState<SendResult | null>(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Done: a moment on "Password changed", then the panel (the guard lets go once resetting is off).
  useEffect(() => {
    if (step !== "done") return;
    const t = window.setTimeout(() => { setResetting(false); navigate("/panel", { replace: true }); }, 1400);
    return () => window.clearTimeout(t);
  }, [step, navigate, setResetting]);

  const send = () => callFunction<SendResult>("account-auth", { action: "resetSend", email: cleanEmail(email) }, { auth: false });

  if (step === "email") {
    const submit = async (e: FormEvent) => {
      e.preventDefault();
      setBusy(true);
      setError(null);
      try {
        setFirst(await send());
        setStep("code");
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setBusy(false);
      }
    };
    return (
      <AuthPage below={<>Remembered it? <Link className="ka-link" to="/login">Log in</Link></>}>
        <form className="ka-form" onSubmit={submit}>
          <Heading title="Reset your password" sub="Enter the email you log in with. We'll send you a 6-digit code." />
          <TextField label="Email" type="email" name="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus />
          {error && <Alert>{error}</Alert>}
          <Submit busy={busy} disabled={!EMAIL.test(cleanEmail(email))}>Send code</Submit>
        </form>
      </AuthPage>
    );
  }

  if (step === "code") {
    return (
      <AuthPage below={<><Link className="ka-link" to="/login">Back to log in</Link></>}>
        <CodeStep email={cleanEmail(email)} firstSend={first} title="Check your email"
          sub="If an account uses this email, we sent it a 6-digit code. Enter it to choose a new password."
          onVerify={async (token) => {
            // The code signs this tab in; the guard must keep the reset on screen until the new password is saved.
            setResetting(true);
            const { error: err } = await supabase.auth.verifyOtp({ email: cleanEmail(email), token, type: "recovery" });
            if (err) {
              setResetting(false);
              throw new Error(authError(err));
            }
            setStep("password");
          }}
          onResend={send}
          onBack={() => { setStep("email"); setFirst(null); }}
          backLabel="Change email" />
      </AuthPage>
    );
  }

  if (step === "password") {
    const valid = passwordStrength(password) !== "weak" && password === confirm;
    const submit = async (e: FormEvent) => {
      e.preventDefault();
      if (!valid) return;
      setBusy(true);
      setError(null);
      try {
        await callFunction("account-auth", { action: "resetSet", password, confirmPassword: confirm });
        // Saving the password ended every session, this one too: sign in with the new password.
        const address = session?.user.email ?? cleanEmail(email);
        const { error: err } = await supabase.auth.signInWithPassword({ email: address, password });
        if (err) throw new Error(`Your password was changed, but signing in again failed (${authError(err)}). Log in with the new password.`);
        setStep("done");
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setBusy(false);
      }
    };
    return (
      <AuthPage>
        <form className="ka-form" onSubmit={submit}>
          <Heading title="Choose a new password" sub={<>For <b>{session?.user.email ?? cleanEmail(email)}</b>. It can't be one of your last five passwords.</>} />
          <NewPasswordFields password={password} setPassword={setPassword} confirm={confirm} setConfirm={setConfirm} labels={["New password", "Repeat password"]} />
          {error && <Alert>{error}</Alert>}
          <Submit busy={busy} disabled={!valid}>Save new password</Submit>
          <button type="button" className="ka-btn quiet" onClick={async () => { setResetting(false); await signOut(); navigate("/login", { replace: true }); }}>Cancel</button>
        </form>
      </AuthPage>
    );
  }

  return (
    <AuthPage>
      <div className="ka-form ka-done" role="status">
        <span className="ka-done-mark" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 22 22"><path d="M5.5 11.4l3.6 3.6 7.4-7.7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </span>
        <Heading title="Password changed" sub="You're signed in. Opening Keept Runner…" />
        <Spinner />
      </div>
    </AuthPage>
  );
}

// ---- Accounts made outside the app ---------------------------------------------------------------------

/** An email account without a profile (made outside the app): a name, once. */
export function FinishSetup() {
  const { refreshProfile, signOut } = useAuth();
  const role = "accountant";
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <div className="auth kr-auth">
      <div className="kr-auth-brand"><Wordmark mark size={24} /></div>
      <div className="dialog kr-light kr-auth-card">
        <div className="win-title"><span>Finish setting up</span></div>
        <div className="dialog-body col gap-12">
          <p className="faint small">Keept Runner is for accountants: one account is one person, with a plan of its own.</p>
          <Field label="Your name or firm"><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
          {error && <Banner tone="err">{error}</Banner>}
        </div>
        <div className="dialog-foot">
          <button className="btn ghost" onClick={() => void signOut()}>Log out</button>
          <button className="btn primary" disabled={busy} onClick={async () => {
            setBusy(true);
            try {
              await callFunction("account-auth", { action: "finish", role, displayName: name });
              await refreshProfile();
            } catch (e) {
              setError(errorMessage(e));
            } finally {
              setBusy(false);
            }
          }}>{busy && <Spinner />}Continue</button>
        </div>
      </div>
    </div>
  );
}
